<template>
  <BaseLayout title="A.R.C Score" :customClasses="['register']" :centerHeading="true">
    <div class="registration-card" :aria-busy="busy ? 'true' : 'false'">
      <h2>Create your ARC account</h2>
      <p class="intro">New to ARC? Get started with your work Google account.</p>

      <p v-if="busy" role="status">{{ creating ? 'Creating and verifying your account…' : 'Checking your account…' }}</p>
      <div v-else-if="visibleError && !visibleError.field" class="registration-error" role="alert">
        <p>{{ visibleError.message }}</p>
        <Button v-if="visibleError.action === 'retry'" text="Check again" btnType="primary" @click="loadAccount" />
        <Button v-else-if="visibleError.action === 'google-login'" text="Sign in with Google again" btnType="primary" @click="signIn" />
        <AccountMigrationNotice v-else-if="visibleError.action === 'migrate-account'" :required="true" />
      </div>

      <template v-if="!busy && !$auth.isAuthenticated && !visibleError">
        <p v-if="!enabled">New account registration is not available in this environment yet.</p>
        <Button v-else text="Continue with Google" btnType="primary" @click="signIn" />
      </template>

      <template v-if="info && !busy">
        <AccountMigrationNotice v-if="info.matches.usernameMatch || migrationChosen" :possibleMatch="info.matches.usernameMatch" :required="migrationChosen" />
        <div v-if="info.matches.candidates.length" class="possible-accounts">
          <h3 v-if="!info.matches.usernameMatch && !migrationChosen">We may have found an existing account.</h3>
          <p v-if="!info.matches.usernameMatch && !migrationChosen">These accounts have the same full name. Do any belong to you?</p>
          <ul aria-label="Possible existing accounts">
            <li v-for="(candidate, index) in info.matches.candidates" :key="index">
              <strong>{{ candidate.name }}</strong>
              <span>{{ candidate.email }}</span>
              <small>{{ candidate.reason }}</small>
            </li>
          </ul>
          <template v-if="!info.matches.usernameMatch && !migrationChosen">
            <button class="text-action" type="button" @click="migrationChosen = true">This may be my account</button>
            <label class="confirmation"><input v-model="dismissedMatches" type="checkbox"> None of these are mine</label>
          </template>
        </div>
        <Button v-if="info.matches.usernameMatch || migrationChosen" text="I’ve migrated — sign in again" btnType="primary" @click="signIn" />
        <form v-else @submit.prevent="createAccount">
          <label for="registration-email">Google work email</label>
          <input id="registration-email" type="email" :value="info.identity.email" readonly>
          <label for="registration-name">Your name</label>
          <input id="registration-name" v-model="name" type="text" required minlength="3" maxlength="255" autocomplete="name"
            :aria-invalid="visibleError && visibleError.field === 'name' ? 'true' : 'false'" aria-describedby="registration-name-error" @input="clearNameError">
          <p v-if="visibleError && visibleError.field === 'name'" id="registration-name-error" class="field-error" role="alert">{{ visibleError.message }}</p>
          <template v-if="info.needsFreshLogin">
            <p>Sign in with Google again to confirm your identity before creating your account.</p>
            <Button text="Confirm with Google" btnType="primary" @click="signIn" type="button" />
          </template>
          <Button v-else text="Create my ARC account" btnType="primary" type="submit" :isDisabled="!canCreate" />
        </form>
      </template>
      <p class="login-link">Already use ARC? <router-link to="/login">Sign in</router-link></p>
    </div>
  </BaseLayout>
</template>

<script>
import Vue from 'vue'
import BaseLayout from '@/views/BaseLayout'
import Button from '@/components/form/Button'
import AccountMigrationNotice from '@/components/auth/AccountMigrationNotice'
import { initializeAuth, inspectRegistration, registerAccount } from '@/utils/authInitializer'
import { requestError } from '@/utils/googleMigration'
import { selfRegistrationEnabled } from '@/utils/googleRegistration'

export default {
  name: 'Register',
  components: { BaseLayout, Button, AccountMigrationNotice },
  data () {
    return { enabled: selfRegistrationEnabled(), info: null, name: '', error: null, busy: false, creating: false, dismissedMatches: false, migrationChosen: false, operation: 0 }
  },
  computed: {
    session () {
      return !this.$auth.loading && !this.$auth.error && this.$auth.isAuthenticated
        ? `${this.$auth.user.sub}:${this.$auth.idTokenClaims?.__raw || ''}` : null
    },
    visibleError () { return this.$auth.error || this.error },
    canCreate () {
      return this.enabled && this.info && !this.busy && !this.visibleError && !this.info.needsFreshLogin && !this.info.matches.usernameMatch &&
        !this.migrationChosen && (!this.info.matches.candidates.length || this.dismissedMatches)
    }
  },
  watch: {
    session: {
      immediate: true,
      handler (session) {
        this.operation++
        this.info = null
        this.error = null
        this.name = ''
        this.busy = false
        this.creating = false
        this.dismissedMatches = false
        this.migrationChosen = false
        if (session) this.loadAccount()
      }
    }
  },
  beforeDestroy () { this.operation++ },
  methods: {
    signIn () {
      return this.$auth.loginWithRedirect({ appState: { targetUrl: this.$auth.targetUrl || '/' } })
    },
    finish () {
      const requested = this.$auth.targetUrl
      const target = typeof requested === 'string' && requested.startsWith('/') && !requested.startsWith('//') && !requested.includes('\\') &&
        !/^\/(login|register)([/?#]|$)/.test(requested) ? requested : '/'
      return this.$router.replace(target).catch(() => {})
    },
    async loadAccount () {
      if (!this.session || this.busy) return
      const operation = ++this.operation
      this.busy = true
      this.error = null
      this.dismissedMatches = false
      this.migrationChosen = false
      try {
        try {
          await initializeAuth(Vue)
          if (operation === this.operation) await this.finish()
          return
        } catch (error) {
          if (error.code !== 'REGISTRATION_REQUIRED') throw error
        }
        const info = await inspectRegistration(Vue)
        if (operation !== this.operation) return
        if (info.account) return this.finish()
        this.info = info
        if (!this.name) this.name = info.identity.name
      } catch (error) {
        if (operation === this.operation) this.error = requestError(error)
      } finally {
        if (operation === this.operation) this.busy = false
      }
    },
    clearNameError () { if (this.error?.field === 'name') this.error = null },
    async createAccount () {
      if (!this.canCreate) return
      const operation = ++this.operation
      this.busy = true
      this.creating = true
      this.error = null
      try {
        await registerAccount(Vue, {
          name: this.name,
          acknowledgedMatches: this.dismissedMatches ? this.info.matches.fingerprint : null
        })
        if (operation === this.operation) await this.finish()
      } catch (error) {
        if (operation !== this.operation) return
        if (error.matches) {
          this.info = { ...this.info, matches: error.matches }
          this.dismissedMatches = false
        } else this.error = requestError(error)
      } finally {
        if (operation === this.operation) { this.busy = false; this.creating = false }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.registration-card {
  max-width: 540px;
  margin: 0 auto;
  padding: 28px;
  border: 1px solid #d4e3f7;
  border-radius: 8px;
  background: #fff;
  h2 { margin: 0 0 12px; font-size: 24px; }
  h3 { font-size: 18px; margin-bottom: 10px; }
  p { text-align: left; font-weight: 400; font-size: 14px; line-height: 1.6; margin: 12px 0 20px; }
  label { display: block; font-size: 14px; margin: 20px 0 8px; }
  input[type='text'], input[type='email'] { box-sizing: border-box; width: 100%; padding: 12px; border: 1px solid #b5c2d3; border-radius: 5px; font: inherit; }
  input[readonly] { background: #f3f7fd; color: #39415b; }
  input:focus-visible, button:focus-visible, a:focus-visible { outline: 3px solid #0158e3; outline-offset: 3px; }
  .button { margin-top: 20px; max-height: none; }
  .login-link { margin: 28px 0 0; }
  .registration-error > p, .field-error { color: #a8232f; }
  .text-action { border: 0; background: none; color: #0158e3; padding: 0; font: inherit; font-size: 14px; text-decoration: underline; cursor: pointer; }
  .confirmation { display: flex; align-items: baseline; gap: 8px; }
  .possible-accounts { margin: 24px 0; }
  ul { list-style: none; padding: 0; margin: 16px 0; }
  li { padding: 12px; background: #f3f7fd; border-radius: 5px; margin-bottom: 8px; overflow-wrap: anywhere; font-size: 14px; }
  li span, li small { display: block; margin-top: 5px; }
  .migration-notice { padding: 18px; }
}
@media (max-width: 600px) {
  .registration-card { padding: 20px; }
}
</style>
